import { STICKER_MIN_FONT_RATIO, STICKER_MIN_FONT_SIZE } from "../constantsV2";
import type { ScaledTokenLine, StickerLayoutSnapshot, TextLine, TextV2Data } from "../typesV2";
import { calculateLineHeight, calculateLineWidths, getMaxLineWidth } from "./measurements";
import { deltaToLines, tokenizeLinesAtBaseScale, wrapScaledTokensToWidth } from "./wrap";

type FindBestStickerLayoutArgs = {
    data: TextV2Data;
    sourceLines: TextLine[];
    hardWidth: number;
    hardHeight: number;
    minFontSize?: number;
    maxFontSize?: number;
};

/**
 * Динамический минимальный шрифт для стикера.
 * Учитывает как абсолютный минимум (STICKER_MIN_FONT_SIZE),
 * так и размер самого стикера (шрифт должен занимать не менее STICKER_MIN_FONT_RATIO высоты).
 */
export const getStickerMinFontSize = (hardWidth: number, hardHeight: number): number => {
    const sizeBasedMin = Math.min(hardWidth, hardHeight) * STICKER_MIN_FONT_RATIO;
    return Math.max(STICKER_MIN_FONT_SIZE, sizeBasedMin);
};

/**
 * Строит StickerLayoutSnapshot из предвычисленных ScaledTokenLine-ов.
 * Все ширины токенов масштабируются на fontSize без вызовов measureTextWidth,
 * что делает эту функцию пригодной для вызова внутри бинарного поиска.
 */
const buildStickerLayoutFromScaled = (
    scaledLines: ScaledTokenLine[],
    data: TextV2Data,
    fontSize: number,
    hardWidth: number,
    allowWordSplit: boolean,
): StickerLayoutSnapshot => {
    const scaledData: TextV2Data = { ...data, fontSize };
    const metrics = calculateLineHeight(scaledData);

    // horizontalPaddingBase — это padding при fontSize=1, т.к. calculateLineHeight(fontSize=1)
    // даёт base-padding, который линейно масштабируется. Но calculateLineHeight зависит от
    // measureText("F"), поэтому мы вызываем его напрямую и используем результат.
    const lines = wrapScaledTokensToWidth(
        scaledLines,
        fontSize,
        hardWidth,
        metrics.horizontalPadding / fontSize,
        allowWordSplit,
    );

    const lineWidths = calculateLineWidths(lines, scaledData, metrics.horizontalPadding);
    const width = getMaxLineWidth(lineWidths);
    const height = lines.length * metrics.lineHeight;

    return {
        fontSize,
        lineHeight: metrics.lineHeight,
        verticalPadding: metrics.verticalPadding,
        horizontalPadding: metrics.horizontalPadding,
        lines,
        lineWidths,
        width,
        height,
    };
};

export const findBestStickerLayout = ({
    data,
    sourceLines,
    hardWidth,
    hardHeight,
    minFontSize = 1,
    maxFontSize = Math.max(1, data.fontSize),
}: FindBestStickerLayoutArgs): StickerLayoutSnapshot => {
    // Токенизируем один раз при fontSize=1. Все последующие итерации бинарного поиска
    // используют масштабированные ширины вместо повторных вызовов measureTextWidth.
    const scaledLines = tokenizeLinesAtBaseScale(sourceLines, data);

    const buildAt = (fontSize: number, allowWordSplit: boolean): StickerLayoutSnapshot =>
        buildStickerLayoutFromScaled(scaledLines, data, fontSize, hardWidth, allowWordSplit);

    // Точность бинарного поиска по fontSize: 0.5px достаточно чтобы не было заметного скачка шрифта
    const FONT_SIZE_PRECISION = 0.5;

    // fitsNoSplit: слово не разбивается — проверяем что и ширина и высота влезают.
    // fitsSplit: при посимвольной разбивке каждый символ гарантированно <= hardWidth, поэтому проверяем только высоту.
    const fitsNoSplit = (layout: StickerLayoutSnapshot) =>
        layout.width <= hardWidth && layout.height <= hardHeight;
    const fitsSplit = (layout: StickerLayoutSnapshot) => layout.height <= hardHeight;

    const findBest = (
        allowWordSplit: boolean,
        fitsFn: (l: StickerLayoutSnapshot) => boolean,
        minCandidateFontSize: number,
        maxCandidateFontSize: number,
    ): StickerLayoutSnapshot => {
        const min = Math.max(1, minCandidateFontSize);
        const max = Math.max(min, maxCandidateFontSize);
        let low = min;
        let high = max;
        let bestLayout: StickerLayoutSnapshot | null = null;

        while (high - low >= FONT_SIZE_PRECISION) {
            const mid = (low + high) / 2;
            const midLayout = buildAt(mid, allowWordSplit);
            if (fitsFn(midLayout)) {
                bestLayout = midLayout;
                low = mid;
            } else {
                high = mid;
            }
        }

        if (bestLayout === null || !fitsFn(bestLayout)) {
            const lowLayout = buildAt(low, allowWordSplit);
            if (fitsFn(lowLayout)) {
                return lowLayout;
            }
        }

        return bestLayout ?? buildAt(min, allowWordSplit);
    };

    // Ищем максимальный fontSize с переносом по словам (allowWordSplit=false).
    // Это моделирует поведение браузера с word-break: normal.
    // Критерий: width <= hardWidth && height <= hardHeight.
    const noSplitBest = findBest(false, fitsNoSplit, minFontSize, maxFontSize);
    if (fitsNoSplit(noSplitBest)) {
        return noSplitBest;
    }

    // С переносом по словам не влезает — есть слово шире стикера.
    // Разрешаем посимвольную разбивку (как word-break: break-all для длинных слов).
    const splitMaxFontSize = Math.max(hardHeight, hardWidth);
    const splitBest = findBest(true, fitsSplit, minFontSize, splitMaxFontSize);
    if (fitsSplit(splitBest)) {
        return splitBest;
    }

    // Не влезает даже при minFontSize — ищем ниже минимума.
    return findBest(true, fitsSplit, 1, minFontSize - 1);
};

export const findBestStickerLayoutFromDelta = ({
    data,
    hardWidth,
    hardHeight,
    minFontSize,
    maxFontSize,
}: Omit<FindBestStickerLayoutArgs, "sourceLines">): StickerLayoutSnapshot => {
    return findBestStickerLayout({
        data,
        sourceLines: deltaToLines(data.delta),
        hardWidth,
        hardHeight,
        minFontSize,
        maxFontSize,
    });
};
